
import { AfterViewInit, Component, ElementRef, inject, ViewChild } from '@angular/core';
import { BreadcrumbService } from 'xng-breadcrumb';
import { Router, ActivatedRoute } from '@angular/router';
import { ToastrService } from 'ngx-toastr';
import { ngxCsv } from 'ngx-csv/ngx-csv';
import dayjs from 'dayjs';
import { NgbDate } from '@ng-bootstrap/ng-bootstrap';
import { TreeNode } from 'src/app/models/tree-node';
import { TasksService } from 'src/app/services/tasks.service';
import { DateService } from 'src/app/shared/services/date.service';
import { IconsService } from 'src/app/shared/services/icons.service';
import { SummaryService } from 'src/app/services/summary.service';
import { SettlementsService } from 'src/app/services/settlements.service';
import { ModalAddBuildingComponent } from 'src/app/components/modals/modal-add-building.component';
import { ModalAddUnitComponent } from 'src/app/components/modals/modal-add-unit.component';
import { Settlement } from 'src/app/models/settlement.model';
import { Building } from 'src/app/models/building.model';
import { STORAGE_KEYS, StorageService } from 'src/app/services/storage.service';
import { TreeViewComponent } from 'src/app/components/tree-view/tree-view.component';
import { formatDiscountValue } from 'src/app/shared/utils/discount-type.utils';
import { formatContractDate, shouldWarnContractExpiration } from 'src/app/shared/utils/contract-term.utils';
import { AuthService } from 'src/app/shared/services/auth.service';
import { finalize } from 'rxjs';

@Component({
  selector: 'app-tenants',
  template: `
    <div class="d-flex mb-4">
      <xng-breadcrumb ></xng-breadcrumb>
    </div>
    <div class="d-flex mt-0 mt-md-4">
      <div class="flex-fill">
        <h1 class="mt-2 mt-md-0">Clientes</h1>
      </div>
    </div>
    <div class="d-flex w-100 mt-4">
      <div class="me-3 d-none d-md-block">
        <ul app-nav-settlements [currentView]="currentView" class="sidebar-zazz list-unstyled"></ul>
      </div>
      <div class="flex-fill mx-2 mx-sm-0" *ngIf="!editSettlement">
        <div class="list-group mb-5">
          <div class="list-group-item py-3">
            <div class="d-flex">
              <div class="flex-fill">
                <h5>
                  <span *ngIf="settlement?.tipo===0">Condominio</span>
                  <span *ngIf="settlement?.tipo===1">Comercial</span>
                </h5>
                <h4>{{settlement?.name}}</h4>
                <p class="mb-2"><small>{{settlement?.address}}, {{settlement?.city}}, {{settlement?.country}}, {{settlement?.state}} {{settlement?.zip}}</small></p>
              </div>
              <div class="ms-auto" style="min-width: 104px;">
                <button 
                  type="button" 
                  class="btn btn-sm btn-outline-secondary w-100" 
                  *ngIf="authService.canEditSettlements()"
                  (click)="editSettlement = !editSettlement"
                >Editar</button>
                <button 
                  type="button" 
                  class="btn btn-outline-info btn-sm mt-2 w-100"
                  data-bs-toggle="offcanvas" 
                  data-bs-target="#auditTrailOffcanvas"
                  (click)="loadAuditTrail()"
                >
                  <fa-icon [icon]="icons.faHistory" class="me-1"></fa-icon>
                  Historial
                </button>
                <button type="button" class="btn btn-outline-secondary btn-sm mt-2 w-100"
                  *ngIf="settlement?.enabled === true && authService.hasPermission('can_edit_settlements')"
                  [disabled]="archivingSettlement" (click)="archiveSettlement()">
                  {{archivingSettlement ? 'Archivando…' : 'Archivar'}}
                </button>
              </div>
            </div>
          </div>

          <div class="list-group-item py-3">
            <div class="d-flex">
              <div>
                <h5>Administración</h5>
                <p class="mb-0">{{settlement?.admin_name}}</p>
                <p class="mb-0"><small>{{settlement?.email}}</small></p>
                <p class="mb-0"><small>{{settlement?.phone}}</small></p>
                <p class="mb-0">{{settlement?.description}}</p>
                <p class="mb-0">Motor de pago <strong style="text-transform: capitalize;">{{settlement?.merchant_id}}</strong></p>
              </div>
              <div class="ms-auto"></div>
            </div>
          </div>

          <app-users-list 
            [settlement]="settlement"
          ></app-users-list>

          <div class="list-group-item py-3" *ngIf="settlement?.settlement_fiscal_attributes as fiscal">
            <h5>Datos fiscales de la administración</h5>
            <table class="table table-sm mb-0" aria-label="Datos fiscales de la administración">
              <tbody>
                <tr><th scope="row">RFC</th><td class="text-end">{{fiscal.rfc || '—'}}</td></tr>
                <tr><th scope="row">Razón social</th><td class="text-end">{{fiscal.razon_social || '—'}}</td></tr>
                <tr><th scope="row">Código postal fiscal</th><td class="text-end">{{fiscal.zip_code || '—'}}</td></tr>
                <tr><th scope="row">Régimen fiscal</th><td class="text-end">{{fiscal.regimen_fiscal || '—'}}</td></tr>
                <tr><th scope="row">Uso CFDI</th><td class="text-end">{{fiscal.uso_cfdi || '—'}}</td></tr>
              </tbody>
            </table>
          </div>

          <div class="list-group-item py-3">
            <h5>Configuración</h5>
            <app-contract-history *ngIf="settlement?.id" [settlementId]="settlement!.id!"></app-contract-history>
            <div class="alert alert-warning mt-3" role="alert" *ngIf="contractExpirationWarning">
              El contrato no tiene renovación automática y vence dentro de los próximos tres meses o ya venció.
            </div>
            <div class="d-flex">                
              <table class="table table-sm" style="font-size: 0.9em;">
                <tbody>
                  <tr>
                    <td>Razón social que emite factura</td>
                    <td class="text-end">{{ settlement?.legal_entity?.business_name || '—' }}</td>
                  </tr>
                  <tr>
                    <td>Temporalidad del contrato</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.contract_term_years !== null && settlement?.contract_term_years !== undefined">
                        {{settlement?.contract_term_years}} años
                      </span>
                      <span *ngIf="settlement?.contract_term_years === null || settlement?.contract_term_years === undefined">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Renovación automática</td>
                    <td class="text-end">{{automaticRenewalLabel}}</td>
                  </tr>
                  <tr>
                    <td>Fecha de terminación del contrato</td>
                    <td class="text-end">{{formatContractDate(settlement?.contract_end_date)}}</td>
                  </tr>
                  <tr>
                    <td>Factor</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.factor">{{settlement?.factor}}</span>
                      <span *ngIf="!settlement?.factor">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Costo administrativo</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.admin_price">{{settlement?.admin_price | currency}}</span>
                      <span *ngIf="!settlement?.admin_price">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Cargos moratorios %</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.interests">{{settlement?.interests}}</span>
                      <span *ngIf="!settlement?.interests">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Costo de reconexión</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.reconexion_fee">{{settlement?.reconexion_fee | currency}}</span>
                      <span *ngIf="!settlement?.reconexion_fee">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Descuento por litro</td>
                    <td class="text-end">
                      {{ formatDiscountValue(settlement?.discount, settlement?.discount_type_id) }}
                    </td>
                  </tr>
                  <tr>
                    <td>Periodicidad</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.periodicity">{{settlement?.periodicity}}</span>
                      <span *ngIf="!settlement?.periodicity">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Días crédito</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.credit">{{settlement?.credit}}</span>
                      <span *ngIf="!settlement?.credit">-</span>
                    </td>
                  </tr>

                  <tr>
                    <td>Inicio de ciclo (día del mes)</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.cycle_day">{{settlement?.cycle_day}}</span>
                      <span *ngIf="!settlement?.cycle_day">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Corte por adeudo (periodos)</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.corte">{{settlement?.corte}}</span>
                      <span *ngIf="!settlement?.corte">-</span>
                    </td>
                  </tr>
                  <tr>
                    <td>Multa por manipulación</td>
                    <td class="text-end">
                      <span *ngIf="settlement?.tampering_fine">{{settlement?.tampering_fine | currency}}</span>
                      <span *ngIf="!settlement?.tampering_fine">-</span>
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <div class="list-group-item py-3">
            <div class="d-flex">
              <h5>Resumen</h5>
            </div>
            <div class="d-flex mt-3">
              <div class="flex-fill text-center">
                <h3 class="mb-0">{{averageGrade | number: '1.2-2'}}  <fa-icon [icon]="icons.faStar" class="text-warning"></fa-icon></h3>
                <p class="mb-0">Calificación</p>
              </div>
              <div class="flex-fill text-center">
                <h3 class="mb-0">{{buildings.length}}</h3>
                <p class="mb-0">Edificios</p>
              </div>
              <div class="flex-fill text-center">
                <h3 class="mb-0">{{countUnits}}</h3>
                <p class="mb-0">Deptos.</p>
              </div>
              <div class="flex-fill text-center">
                <h3 class="mb-0">{{countTanques}}</h3>
                <p class="mb-0">Tanques</p>
              </div>                  
            </div>
          </div>
          <div class="list-group-item py-3">
            <p class="text-center text-muted text-sm mb-0" style="font-size: 0.7em;">La cartera se consideran todos los recibos emitidos generados.</p>
            <div class="row">
              <div class="col-8 offset-2 col-sm-4 offset-sm-0 text-center" #portafolioChart id="portafolioChart" *ngIf="portafolio.past_due">
                <app-pie-chart-portfolio [data]="portafolio"></app-pie-chart-portfolio>
              </div>                  
              <div class="col-12 col-sm-8 text-center mt-3">
                <div class="d-flex">
                  <div class="flex-fill text-center">
                    <h4 [countUp]="portafolio.past_due" [currency]="'$'"></h4>
                    <p>Cartera vencida</p>
                  </div>
                  <div class="flex-fill text-center">
                    <h4 [countUp]="portafolio.paid" [currency]="'$'"></h4>
                    <p>Cartera corriente</p>
                  </div>
                </div>
                <div class="d-flex">
                  <div class="flex-fill text-center">
                    <h4 [countUp]="portafolio.current" [currency]="'$'"></h4>
                    <p>Cartera no vencida</p>
                  </div>
                  <div class="flex-fill text-center">
                    <h4 [countUp]="portafolio.liters" [currency]="''"></h4>
                    <p>Litros</p>
                  </div>
                </div>
              </div>

              <h5>Clientes con más adeudos (Top 3)</h5>
              <table class="table table-striped table-sm w-100" style="font-size: 0.8em;">
                <thead>
                  <tr>
                    <th>Dept.</th>
                    <th># Recibo</th>
                    <th class="text-center">Total</th>
                    <th class="text-center">Lts.</th>
                  </tr>
                </thead>
                <tbody>
                  <tr *ngFor="let ov of portafolio.overview">
                    <td>{{ov.unit_name}}</td>
                    <td>#{{ov.id}} {{ov.reference}}</td>
                    <td class="text-center">{{ov.total | currency}}</td>
                    <td class="text-end">{{ov.lt | number: '1.2-2'}} Lts.</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
          <!-- div class="list-group-item py-3">
            <div class="d-flex mt-3" *ngIf="consumption_consumptions.length>0">
              <div>
                <app-line-chart-consumption
                  [data]="consumption_consumptions"
                  [start_date]="payload.start_date"
                  [end_date]="payload.end_date"
                ></app-line-chart-consumption>

                <app-index-chart
                  [data]="consumption_data"
                  [consumptions]="consumption_consumptions"
                  [start_date]="fromDate"
                  [end_date]="toDate"
                  [tanque]="building?.tanques[0]"
                  [summary]="consumption_data"
                ></app-index-chart>
              </div> 
            </div> 
          </div -->
          <div class="list-group-item py-3">
            <div class="d-flex">
              <div>
                <div class="btn-group position-relative" (click)="$event.stopPropagation()" role="group" aria-label="Basic example">
                  <button #1mes type="button" class="btn btn-sm btn-outline-secondary active" (click)="updateFilter(1)">1 mes</button>
                  <button #3mes type="button" class="btn btn-sm btn-outline-secondary" (click)="updateFilter(3)">3 meses</button>
                  <button #6mes type="button" class="btn btn-sm btn-outline-secondary" (click)="updateFilter(6)">6 meses</button>
                  <button #12mes type="button" class="btn btn-sm btn-outline-secondary" (click)="updateFilter(12)">1 año</button>
                  <button #custom type="button" class="btn btn-sm btn-outline-secondary" (click)="datepicker.toggle(); $event.stopPropagation()">
                    Filtrar
                    
                      <input
                        #dpFromDate
                        class="d-none"
                        ngbDatepicker
                        #datepicker="ngbDatepicker"
                        [autoClose]="false"
                        (dateSelect)="onDateSelection($event)"
                        [displayMonths]="2"
                        [dayTemplate]="t"
                        outsideDays="hidden"
                        [startDate]="fromDate!"
                        tabindex="-1"
                        (click)="$event.stopPropagation()"
                      />
                      <ng-template #t let-date let-focused="focused">
                      <span
                        class="custom-day"
                        [class.focused]="focused"
                        [class.range]="isRange(date)"
                        [class.faded]="isHovered(date) || isInside(date)"
                        [class.selected]="date.equals(fromDate) || (toDate && date.equals(toDate))"
                        (mouseenter)="hoveredDate = date"
                        (mouseleave)="hoveredDate = null"
                        (click)="onDateSelection(date); $event.stopPropagation()"
                      >
                        {{ date.day }}
                      </span>
                    </ng-template>
                  </button>
                </div>
              </div>
              <div class="ms-auto text-end">
                <button type="button" class="btn btn-outline-secondary btn-sm" (click)="generateStatement()">Edo. de cuenta</button><br>
              </div>
              <div class="ms-auto text-end">
                <button type="button" class="btn btn-outline-secondary btn-sm" (click)="generateReport()">Generar Reporte</button>
              </div>
            </div>
          </div>

          

          <div class="list-group-item py-3">
          <div class="d-flex">
            <h5>Tareas</h5>
          </div>
          <div class="d-flex">                
            <table class="table table-sm table-striped" style="font-size: 0.9em;">
              <tr class="border-bottom">
                <th>#</th>
                <th>Tarea</th>
                <th class="text-center">Calificación</th>
                <th class="text-center">Fecha</th>
                <th class="text-center">Completada</th>
              </tr>
              <tr *ngFor="let grade of grades">
                <td><a routerLink="/maintenances/tasks/review/{{grade?.id}}"># {{grade?.id}}</a></td>
                <td>{{grade?.name}}</td>
                <td class="text-center">{{grade?.average_grade | number : '1.2-2'}}</td>
                <td class="text-center">{{grade?.due_date | date: 'dd/MM/YY HH:mm'}}</td>
                <td class="text-center">
                  <span *ngIf="grade?.completed">{{grade?.completed_at | date: 'dd/MM/YY HH:mm' }}</span>
                  <span *ngIf="!grade?.completed">Pendiente</span>
                </td>
              </tr>
            </table>
          </div>
          </div>
        </div>
      </div>
      <div class="flex-fill mx-2 mx-sm-0" *ngIf="editSettlement">
        <div class="list-group mb-5">
          <div class="list-group-item py-3">
            <div class="d-flex">
              <app-form-settlement
                [settlement]="settlement"
                [profile]="profile"
                [permissions]="permissions"
                (onDismiss)="editSettlement = !editSettlement"                
              ></app-form-settlement>
            </div>
          </div>
        </div>
      </div>
      <div class="flex-grow-2 ms-3" style="width: 330px; min-width: 330px;">
        <div class="list-group mb-5">              
          <div class="list-group-item py-3">
            <div class="d-flex mb-2">
              <div><h5 class="mt-2">Estructura del condominio</h5></div>
              <div class="ms-auto">
                <button type="button" 
                  class="btn btn-outline-secondary btn-sm" 
                  [disabled]="loading" 
                  (click)="addBuilding()"
                ><fa-icon [icon]="icons.faPlus" class="me-2"></fa-icon> Edificio
                </button>
              </div>
            </div>
            <div>
              <app-tree-view #treeView
                [nodes]="treeData" 
                [settlementId]="settlement?.id"
                (addUnitData)="addUnit($event)"
                (floorData)="addFloor($event)"
                (floorEdit)="editFloor($event)"
                (updateBuildingData)="updateBuilding($event)"
                (updateDismiss)="updateDismissed()"
              ></app-tree-view>
              <p class="text-muted text-center my-5" *ngIf="treeData.length===0">No existen registros.</p>
            </div>
            <div class="text-center my-5" *ngIf="loading">
              <fa-icon [icon]="icons.faCircleNotch" size="3x" animation="spin" class="text-secondary"></fa-icon>
              <p class="text-muted mt-2">Cargando...</p>
            </div>
          </div>
        </div>
      </div>
    </div>
      
    <app-modal-add-building 
      [settlement]="settlement"
      [building]="selectedBuilding"
      (onBuildingCreated)="createBuilding($event)"
      (updateDismiss)="updateDismissed()"
    ></app-modal-add-building>
    <app-modal-add-unit
      [floor_id]="floor_id"
      [settlement]="settlement"
      (updateDismiss)="updateDismissed()"
    ></app-modal-add-unit>
    <app-audit-trail-offcanvas
      [auditData]="auditTrailData"
      [loading]="auditTrailLoading"
      objectType="settlement"
      [customRenderer]="true"
    ></app-audit-trail-offcanvas>
  `,
  styles: [`
    .custom-day {
      text-align: center;
      padding: 0.185rem 0.25rem;
      display: inline-block;
      height: 2rem;
      width: 2rem;
      transform: translate3d(0,0,0)
    }
    .custom-day.focused {
      background-color: #e6e6e6;
    }
    .custom-day.range,
    .custom-day:hover {
      background-color: #0d6efd;
      color: white;
    }
    .custom-day.faded {
      background-color: rgba(13, 110, 253, 0.5);
      color: white;
    }
    .custom-day.selected {
      background-color: #0d6efd;
      color: white;
      font-weight: bold;
    }
    ::ng-deep .ngb-dp-container {
      padding: 4px;
      background-color: #fff;
      border: 1px solid rgba(0,0,0,.15);
      border-radius: 4px;
      box-shadow: 0 2px 4px rgba(0,0,0,.15);
    }
    ::ng-deep ngb-datepicker {
      top: 100% !important;
      left: 0 !important;
    }
    ::ng-deep .dropdown-menu {
      transform: none !important;
      top: 100% !important;
      left: 0 !important;
      margin-top: 2px !important;
    }

    ::ng-deep .dropdown-menu[style] {
      transform: none !important;
    }

    .btn-group .position-relative {
      position: static;
    }

    ::ng-deep ngb-datepicker.dropdown-menu {
      position: absolute !important;
      transform: none !important;
      top: 100% !important;
      left: auto !important;
    }
  `],
})
export class TenantsComponent implements AfterViewInit {
  readonly authService = inject(AuthService);
  archivingSettlement = false;
  readonly formatDiscountValue = formatDiscountValue;
  readonly formatContractDate = formatContractDate;
  @ViewChild(ModalAddBuildingComponent, { static: true }) modalAddBuildingComponent !: ModalAddBuildingComponent;
  @ViewChild('treeView', { static: true }) treeViewComponent !: TreeViewComponent;
  @ViewChild(ModalAddUnitComponent, { static: true }) modalAddUnitComponent !: ModalAddUnitComponent;
  @ViewChild('1day') oneDay !: ElementRef;
  @ViewChild('yesterday') yesterday !: ElementRef;
  @ViewChild('1mes') oneMonth !: ElementRef;
  @ViewChild('3mes') threeMonth !: ElementRef;
  @ViewChild('6mes') sixMonth !: ElementRef;
  @ViewChild('12mes') twelveMonth !: ElementRef;
  @ViewChild('custom') custom !: ElementRef;

  settlement: Settlement | null = null;
  buildings: Building[] = [];
  building: Building | null = null;
  grades: any = [];

  loading: boolean = false;
  selectedBuilding: any = null;
  editSettlement: boolean = false;
  debugMode: boolean = true; // Enable debug mode for testing

  // Audit Trail properties
  auditTrailData: any = null;
  auditTrailLoading: boolean = false;

  currentView: string = '';

  // buildingForm: FormGroup = new FormGroup({});
  // floorForm: FormGroup = new FormGroup({});
  // unitForm: FormGroup = new FormGroup({});
  // period = 1;
  // searchText = '';

  fromDate: any;
  toDate: any;
  payFilterPrefs: any = {};

  addingUtility: boolean = false;
  addingFloor: boolean = false;
  addingUnit: boolean = false;

  floor_id: number = 0;
  countUnits: number = 0;
  countTanques: number = 0;
  treeData: TreeNode[] = [];

  payload: any = {
    year: 2024,
    month: [1,2,3,4,5],
    state: [1,2],
  };

  averageGrade: number = 0;

  portafolio: any = {};
  profile: any = null;
  permissions: any = null;
  consumption_data: any[] = [];
  consumption_consumptions: any[] = [];
  consumption_tanques: any[] = [];

  hoveredDate: NgbDate | null = null;
  storageService = inject(StorageService);

  get automaticRenewalLabel(): string {
    if (this.settlement?.automatic_renewal === true) {
      return 'Sí';
    }

    if (this.settlement?.automatic_renewal === false) {
      return 'No';
    }

    return '-';
  }

  get contractExpirationWarning(): boolean {
    return shouldWarnContractExpiration(
      this.settlement?.contract_end_date,
      this.settlement?.automatic_renewal
    );
  }

  constructor(
    private store : SettlementsService,
    private taskService: TasksService,
    private route: Router,
    private activateRoute: ActivatedRoute,
    private toastr: ToastrService,
    private summ: SummaryService,
    private breadcrumbService: BreadcrumbService,
    private dateService: DateService,
    public icons: IconsService
  ) {
    this.profile = this.storageService.getItem(STORAGE_KEYS.PROFILE) ? this.storageService.getItem(STORAGE_KEYS.PROFILE).profile : null;
    this.permissions = this.storageService.getItem(STORAGE_KEYS.PERMISSIONS) ? this.storageService.getItem(STORAGE_KEYS.PERMISSIONS) : null;
    this.editSettlement = this.activateRoute.snapshot.queryParamMap.get('edit') === 'true';
    const today = dayjs();
    const monthStart = today.startOf('month');
    const monthEnd = today.endOf('month');
    this.fromDate = new NgbDate(monthStart.year(), monthStart.month() + 1, monthStart.date());
    this.toDate = new NgbDate(monthEnd.year(), monthEnd.month() + 1, monthEnd.date());
    this.payFilterPrefs.created_at_since = this.formatNgbDate(this.fromDate);
    this.payFilterPrefs.created_at_to = this.formatNgbDate(this.toDate);

    this.activateRoute.params.subscribe(params => {
      this.store.getSettlement(params['id'])
        .subscribe((settlement: any) => {
          this.loading = true;
          this.settlement = settlement;

          // Set currentView based on settlement.tipo
          this.currentView = settlement?.tipo === 0 ? 'condominio' : settlement?.tipo === 1 ? 'comercial' : 'settlement';
          this.storageService.saveItem('tenantType', this.currentView);

          this.loadBuilding(this.settlement?.id);
          this.loadPortafolio();
          this.loadTankSummary();
          this.loadTasks();
        });
    });
  }

  ngAfterViewInit(): void {
    this.breadcrumbService.set('@settlementName', `${this.settlement?.name}`);
  }

  protected loadTasks() {
    console.log('Loading tasks for settlement:', this.payFilterPrefs);
    this.taskService.getTechStatsSummary({
      settlement_id: this.settlement?.id,
      due_date_since: this.payFilterPrefs.created_at_since,
      due_date_to: this.payFilterPrefs.created_at_to
    }).subscribe((data: any) => {
      this.grades = data.tasks;
      this.calculateAverageGrade();
    });
  }

  private calculateAverageGrade(): void {
    if (!this.grades || this.grades.length === 0) {
      this.averageGrade = 0;
      return;
    }
    
    const sum = this.grades.reduce((total: number, task: any) => {
      // Include tasks with grade of 0 as well as those with positive grades
      if (task.average_grade !== null && task.average_grade !== undefined) {
        return total + parseFloat(task.average_grade || 0);
      }
      return total;
    }, 0);
    
    const validGradesCount = this.grades.filter((task: any) => 
      task.average_grade !== null && task.average_grade !== undefined
    ).length;
    
    this.averageGrade = validGradesCount > 0 ? sum / validGradesCount : 0;
  }

  protected loadBuilding(id: any) {
    this.buildings = [];
    this.store.getBuildingsBySettlement(id).subscribe({
      next: (data: any) => {
        this.buildings = data.sort((a: any, b: any) => a.id - b.id);        
        data.map((building: any) => {
          building.floors.map((floor: any) => {
            this.countUnits += floor.units.length
          });
          this.countTanques += building.tanques.length;
        });
        this.buildingsTree();
        this.loading = false;
      },
      error: (e: any) => {
        this.toastr.error('Edificios no pudieron ser cargados.', 'Error');
        this.loading = false;
      }
    });
  }

  protected loadPortafolio(): void {
    this.store.getBillsPortafolioSettlement({ 
      profile_id: this.profile.id, 
      settlement_id: this.settlement?.id 
    }).subscribe({
      next: (response: any) => {
        this.portafolio = response;
      },
      error: (err: any) => {
        this.toastr.error('Error al cargar la cartera.', 'Error');
      }
    });
  }

  loadTankSummary() {
    this.summ.getConsumeSummaryBySettlementMonth({
      settlement_id: this.settlement?.id,
      year: 2024,
      month: [1,2,3,4,5,6,7,8,9,10],
    }).subscribe((data: any) => {
      console.log('getTanqueSummary data => ', data);
      this.consumption_data = data;
      this.consumption_consumptions = data.consumptions;
      this.consumption_tanques = data.readings;
    });
  }

  protected buildingsTree(): void {
    this.treeData = [];
    this.buildings.map((building: any) => {
      this.treeData.push({
        id: building.id,
        name: building.name,
        type: 0,
        tanques: building.tanques,
        settlement_id: this.settlement?.id,
        isExpanded: true,
        children: building.floors.map((floor: any) => { 
          return {
            id: floor.id,
            name: `Piso ${floor.name}`,
            type: 1,
            isExpanded: true,
            children: floor.units.map((unit: any) => {
              return {
                id: unit.id,
                name: unit.name,
                status: unit.status,
                type: 2,
                settlement_id: this.settlement?.id,
                tenants: unit.tenants,
                gas_meters: unit.gas_meters,
                subscriptions: unit.subscriptions,
                children: [],
              }
            }),
          }
        }),
      });
    });
  }

  /*createUnit(form: any) {
    this.store.createUnit(form).subscribe({
      next: (data: any) =>  {
        this.toastr.success('Departamento se creó correctamente.', 'Éxito');
        this.addingUnit = false;
        // this.setBuilding(this.selectedBuilding.id);
        window.location.reload();
      },
      error: (error: any) => {
        this.toastr.error('Departamento no pudo ser creada.', 'Error');
      }
    });
  }*/

  createBuilding(event: any) {}

  protected updateBuilding(event: any): void {
    this.selectedBuilding = this.buildings.find((building: any) => building.id == event);
    const modalTemplate: any | undefined = this.modalAddBuildingComponent;
    if (modalTemplate) {
      modalTemplate.openModal(modalTemplate['modalBuilding'], 'edition');
    }    
  }

  protected addBuilding(): void {
    const modalTemplate = this.modalAddBuildingComponent;
    modalTemplate.buildBuildingForm();
    if (modalTemplate) {
      modalTemplate.openModal(modalTemplate['modalBuilding']);
    }
  }

  protected addFloor(event: any): void {
    if (!event) {
      this.toastr.error('Invalid building information', 'Error');
      return;
    }
    
    this.selectedBuilding = this.buildings.find((building: any) => building.id == event);    
    if (!this.selectedBuilding) {
      this.toastr.error('Building not found', 'Error');
      return;
    }
  }

  protected editFloor(event: any): void {
    if (!event) {
      this.toastr.error('Invalid floor information', 'Error');
      return;
    }

    let targetBuilding: any = null;
    let targetFloor: any = null;
    
    this.buildings.forEach(building => {
      if (building.floors) {
        building.floors.forEach(floor => {
          if (floor.id === event) {
            targetBuilding = building;
            targetFloor = floor;
          }
        });
      }
    });
    
    if (!targetBuilding || !targetFloor) {
      this.toastr.error('Floor not found', 'Error');
      return;
    }
    
    this.selectedBuilding = targetBuilding;    
    const modalTemplate = this.treeViewComponent ? this.treeViewComponent : null;
    if (modalTemplate) {
      const modalAddFloor = modalTemplate['modalAddFloorComponent'];
      modalAddFloor.building = this.selectedBuilding;
      modalAddFloor.floorForm.patchValue({
        id: targetFloor.id,
        name: targetFloor.name,
        building_id: targetBuilding.id
      });
      modalAddFloor.openModal(modalAddFloor['modalAddFloor'], 'edition');
    } else {
      this.toastr.error('Floor editing modal not found', 'Error');
    }
  }

  protected addUnit(event: any): void {
    if (!event) {
      this.toastr.error('Invalid floor information', 'Error');
      return;
    }
    
    let targetFloor;
    this.buildings.forEach(building => {
      if (building.floors) {
        building.floors.forEach(floor => {
          if (floor.id === event) {
            targetFloor = floor;
            this.selectedBuilding = building; // Set the selected building
            this.floor_id = event; // Set the floor_id for the modal
          }
        });
      }
    });

    if (!targetFloor) {
      this.toastr.error('Piso no encontrado', 'Error');
      return;
    }

    const modalTemplate: any | undefined = this.modalAddUnitComponent;
    if (modalTemplate) {
      modalTemplate.floor_id = event;
      modalTemplate.openModal(modalTemplate['modalAddUnit']);
    } else {
      this.toastr.error('Modal no encontrado', 'Error');
    }
  }

  protected updateDismissed(): void {
    this.loadBuilding(this.settlement?.id);
  }

  protected generateReport(): void {
    console.log('Generating report for settlement:', this.settlement?.id);
    console.log('Pay filter preferences:', this.payFilterPrefs);
    this.payFilterPrefs.sort = 'id';
    this.payFilterPrefs.order = 'desc';
    // TODO: ink the tank to the bill
    this.store.getBillsBySettlement(this.settlement?.id, this.payFilterPrefs)
      .subscribe((response: any) => {
        console.log('getBillsBySettlement response => ', response);
        let bills: any[] = [];
        response.data.map((bill: any) => {
            bills.push({
              id: bill.id,
              reference: bill?.reference ? bill?.reference : '-',
              description: bill?.description ? bill?.description : '-',
              settlement_name: bill?.settlement_name,
              building_name: bill?.building_name,
              floor_name: bill?.floor_name,
              unit_name: bill?.unit_name,
              status: bill?.status ? 'Pagado' : 'Pendiente',
              consumo_m3: bill?.consumo_m3 ? bill?.consumo_m3.toFixed(2) : '-',
              consumo_lt: bill?.meter_consumption ? (bill?.meter_consumption).toFixed(2) : '-',
              consumo_periodo: bill?.consumption_total ? bill?.consumption_total.toFixed(2) : '-',
              sub_total: bill?.sub_total ? `$ ${bill?.sub_total.toFixed(2)}` : '-',
              total: `$ ${bill?.total.toFixed(2)}`,
              days: bill?.days || '-',
              read_prev: bill?.meter_previous_read,
              read_curr: bill?.meter_current_read,
              discount: bill?.discount || '-',
              periodo_inicio: bill?.periodo_inicio ? this.dateService.formatYMD(bill?.periodo_inicio) : 'No disponible',
              periodo_fin: bill?.periodo_fin ? this.dateService.formatYMD(bill?.periodo_fin) : 'No disponible',
              due_date: bill?.due_date ? this.dateService.formatYMD(bill?.due_date) : 'No disponible',
              paid_date: bill?.paid_date ? this.dateService.formatYMD(bill?.paid_date) : 'No pagado',
              created_at: bill?.created_at ? this.dateService.formatYMD(bill?.created_at) : 'No disponible',
              updated_at: bill?.updated_at ? this.dateService.formatYMD(bill?.updated_at) : 'No disponible',
              admin_price: `$ ${bill?.admin_fee_total || 0}`,
              interests: `$ ${bill?.late_fee_total || 0}`,
              taxes: `$ ${bill?.taxes || 0}`,
              recargos: `$ ${bill?.service_total || 0}`,
              consumo_total: bill?.consumption_total,
              state: bill?.state == 1 ? 'Pendiente' : bill?.state == 2 ? 'Pagado' : 'Cancelado',
              invoiced: bill?.invoiced ? 'Sí' : 'No',
              invoice_id: bill?.invoice_id ? bill?.invoice_id : '-',
              gas_price: bill?.gas_price || '-',
              meter_read_date: bill?.meter_read_date ? this.dateService.formatYMD(bill?.meter_read_date) : 'No disponible'
            });
        });

        let options = {
          title: `Reporte de condominio ${this.settlement?.name}`,
          fieldSeparator: ',',
          quoteStrings: '"',
          decimalSeparator: '.',
          showLabels: true,
          noDownload: false,
          showTitle: true,
          useBom: true,
          headers: [
            "# Recibo",
            "Referencia",
            "Descripción",
            "Condominio",
            "Edificio",
            "Piso",
            "Departamento",
            "Estatus",
            "Consumo M3",
            "Consumo LT",
            "Consumo Periodo",
            "Sub total",
            "Total",
            "Días",
            "Lectura prev.",
            "Lectura actual",
            "Descuento",
            "Periodo inicio",
            "Periodo fin",
            "Vencimiento",
            "Fecha pago",
            "Creado",
            "Actualizado",
            "Admin.",
            "Moratorios",
            "IVA",
            "Recargos",
            "Consumo total",
            "Estado",
            "Facturado",
            "# Factura",
            "Precio gas",
            "Fecha lectura medidor"
          ]
        };

        new ngxCsv(bills, 'Reporte Condominio', options);
      });
  }

  generateStatement() {
    this.store.getAccountStatementBySettlement(this.payload)
      .subscribe({
        next: (response: any) => {
          console.log('getAccountStatementBySettlement response => ', response);
        }, error: (error: any) => {
          console.log('error => ', error);
        }
      });
  }

  updateFilter(mes: number) {
    const end = dayjs();
    const start = end.subtract(mes, 'month').startOf('month');
    const endOfMonth = end.endOf('month');

    this.fromDate = new NgbDate(start.year(), start.month() + 1, start.date());
    this.toDate = new NgbDate(endOfMonth.year(), endOfMonth.month() + 1, endOfMonth.date());

    this.payFilterPrefs.created_at_since = this.formatNgbDate(this.fromDate);
    this.payFilterPrefs.created_at_to = this.formatNgbDate(this.toDate);

    this.loadTasks();

    if (mes == 1) {
      this.oneMonth.nativeElement.classList.add('active');
      this.threeMonth.nativeElement.classList.remove('active');
      this.sixMonth.nativeElement.classList.remove('active');
      this.twelveMonth.nativeElement.classList.remove('active');
    }
    if (mes == 3) {
      this.oneMonth.nativeElement.classList.remove('active');
      this.threeMonth.nativeElement.classList.add('active');
      this.sixMonth.nativeElement.classList.remove('active');
      this.twelveMonth.nativeElement.classList.remove('active');
    }
    if (mes == 6) {
      this.oneMonth.nativeElement.classList.remove('active');
      this.threeMonth.nativeElement.classList.remove('active');
      this.sixMonth.nativeElement.classList.add('active');
      this.twelveMonth.nativeElement.classList.remove('active');
    }
    if (mes == 12) {
      this.oneMonth.nativeElement.classList.remove('active');
      this.threeMonth.nativeElement.classList.remove('active');
      this.sixMonth.nativeElement.classList.remove('active');
      this.twelveMonth.nativeElement.classList.add('active');
    }
  }

  onDateSelection(date: NgbDate) {
    if (!this.fromDate && !this.toDate) {
			this.fromDate = date;
		} else if (this.fromDate && !this.toDate && date) {
      this.toDate = date;
      if (this.fromDate.after(this.toDate)) {
        // If fromDate is after toDate, swap them
        const temp = this.fromDate;
        this.fromDate = this.toDate;
        this.toDate = temp;
      }
		} else {
			this.toDate = null;
			this.fromDate = date;
		}

    if (this.fromDate && this.toDate) {
      this.payFilterPrefs.created_at_since = this.formatNgbDate(this.fromDate);
      this.payFilterPrefs.created_at_to = this.formatNgbDate(this.toDate);
      // this.loadBills(this.payFilterPrefs);
    }
	}

  private formatNgbDate(date: NgbDate): string {
    return dayjs(new Date(date.year, date.month - 1, date.day)).format('YYYY-MM-DD');
  }

	isHovered(date: NgbDate) {
    return (
      this.fromDate && 
      !this.toDate && 
      this.hoveredDate && 
      date.after(this.fromDate) && 
      date.before(this.hoveredDate)
    );
  }

    isRange(date: NgbDate) {
    return (
      date.equals(this.fromDate) ||
      (this.toDate && date.equals(this.toDate)) ||
      this.isInside(date) ||
      this.isHovered(date)
    );
  }

  isInside(date: NgbDate) {
    return this.toDate && 
          date.after(this.fromDate) && 
          date.before(this.toDate);
  }

  loadAuditTrail(): void {
    if (!this.settlement?.id) {
      this.toastr.error('No se puede cargar el historial sin un ID de asentamiento válido', 'Error');
      return;
    }

    this.auditTrailLoading = true;
    this.auditTrailData = null;

    this.store.getSettlementAuditTrail(this.settlement.id).subscribe({
      next: (data: any) => {
        console.log('Settlement audit trail data:', data);
        this.auditTrailData = data;
        this.auditTrailLoading = false;
      },
      error: (err: any) => {
        console.error('Error loading settlement audit trail:', err);
        this.toastr.error('Error al cargar el historial de cambios', 'Error');
        this.auditTrailLoading = false;
      }
    });
  }

  archiveSettlement(): void {
    const settlement = this.settlement;
    if (!settlement?.id || settlement.enabled !== true || this.archivingSettlement
      || !this.authService.hasPermission('can_edit_settlements')) return;
    if (!confirm(`¿Archivar ${settlement.name}? Dejará de aparecer en la lista de clientes activos.`)) return;

    this.archivingSettlement = true;
    this.store.updateSettlementEnabled(settlement.id, false)
      .pipe(finalize(() => this.archivingSettlement = false))
      .subscribe({
        next: () => {
          this.settlement = { ...settlement, enabled: false };
          this.toastr.success('Cliente archivado. Puedes consultarlo en la vista de archivados.');
        },
        error: () => this.toastr.error('No fue posible archivar el cliente.')
      });
  }
}
